import { Fragment, useMemo, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import {
  AlertTriangle,
  BadgeCheck,
  Ban,
  Bell,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  FileCheck2,
  FileDown,
  FilePenLine,
  FileUp,
  Loader2,
  MailCheck,
  ReceiptText,
  RefreshCw,
  Search,
  Send,
  Undo2,
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { ConfirmDialog, cn, showToast } from '@my/ui';
import {
  tmsApi,
  useCancelInvoice,
  useSetInvoiceExport,
  useInvoices,
  useSendInvoiceEmail,
  useSetInvoiceStatus,
} from '@my/tms-api';
import { directoryApi, resolveEmailSender, type EmailConnection } from '@my/directory-api';
import { useOrganisationEmailConnections } from '@my/directory-api/hooks';
import type { InvoiceStatusValue, InvoiceSummary } from '@my/tms-api';
import { RowActionsMenu, type RowAction } from '@/components/RowActionsMenu';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { useInvoiceViewStore } from '@/stores/invoiceView';
import type { InvoiceStatus } from '../lib/types';
import {
  AssistantButton,
  CustomerGroupHeader,
  GroupToggle,
  StatusBadge,
  ToolbarDivider,
} from './shared';
import { DocumentPdf } from '@/features/settings/documents/render/DocumentPdf';
import { useInvoiceDocumentSource } from '../lib/useInvoiceDocumentSource';
import { InvoiceDetails } from './InvoiceDetails';
import { CreditNoteBuilder } from './CreditNoteBuilder';
import { DraftInvoiceEditor } from './DraftInvoiceEditor';
import { useBillingRecipients, type BillingRecipients } from '../lib/useBillingRecipients';

const UNKNOWN_BILLING: BillingRecipients = { state: 'unknown' };

const FILTERS: readonly (InvoiceStatus | 'All')[] = [
  'All',
  'Draft',
  'Issued',
  'Sent',
  'Paid',
  'Overdue',
  'Cancelled',
];

/**
 * Statuses that accumulate over time and need a bounded date window so the
 * query can't run forever. Draft + Overdue are current/actionable — always
 * shown in full, no date bound.
 */
const DATE_BOUNDED: ReadonlySet<string> = new Set(['All', 'Issued', 'Sent', 'Paid', 'Cancelled']);

/** Effective status for display/filtering — derived Overdue wins over the stored status. */
function effectiveStatus(i: InvoiceSummary): InvoiceStatus {
  return i.isOverdue ? 'Overdue' : i.status;
}

function formatMoney(v: number, iso: string | null | undefined): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: iso || 'EUR' }).format(
      v,
    );
  } catch {
    return v.toFixed(2);
  }
}

/** ISO `yyyy-MM-dd` → `dd/mm/yy`, or "—". */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Default date window = the current calendar month, so the list is bounded out of the box. */
function defaultRange(): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const now = new Date();
  return {
    from: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

/**
 * Invoice-summary tab — every generated invoice, grouped by customer (or flat),
 * filterable by status + free text + date window. Row actions are status-aware
 * (Issue / Send / Resend / Mark paid / Chase) and hit the real status endpoint.
 */
export function InvoiceSummaryTab({ onOpenAssistant }: { onOpenAssistant: () => void }) {
  // The invoice whose details are open under its row — one at a time.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggleDetails = (id: string) => {
    setCreditingId(null);
    setEditingId(null);
    setExpandedId((cur) => (cur === id ? null : id));
  };
  // The draft being edited — its editor opens under the row.
  const [editingId, setEditingId] = useState<string | null>(null);
  const startEdit = (inv: InvoiceSummary) => {
    setExpandedId(null);
    setCreditingId(null);
    setEditingId(inv.id);
  };
  // The invoice a credit note is being raised for — its builder opens under the row.
  const [creditingId, setCreditingId] = useState<string | null>(null);
  const startCredit = (inv: InvoiceSummary) => {
    setExpandedId(null);
    setEditingId(null);
    setCreditingId(inv.id);
  };
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const invoicesQuery = useInvoices(orgId || null);
  const setStatus = useSetInvoiceStatus(orgId || null);
  const docSource = useInvoiceDocumentSource(orgId);

  const grouped = useInvoiceViewStore((s) => s.grouped);
  const setGrouped = useInvoiceViewStore((s) => s.setGrouped);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(() => defaultRange().from);
  const [to, setTo] = useState(() => defaultRange().to);

  const dateBounded = DATE_BOUNDED.has(filter);
  const invoices = useMemo(() => invoicesQuery.data ?? [], [invoicesQuery.data]);

  // Every customer in the list, not just the filtered rows, so the warning
  // doesn't flicker in and out as filters change the visible set.
  const customerIds = useMemo(() => invoices.map((i) => i.customerPartyId), [invoices]);
  const billingByParty = useBillingRecipients(customerIds);
  const billingOf = (partyId: string) => billingByParty.get(partyId) ?? UNKNOWN_BILLING;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromMs = dateBounded && from ? new Date(from).getTime() : null;
    const toMs = dateBounded && to ? new Date(`${to}T23:59:59`).getTime() : null;
    return invoices.filter((i) => {
      if (filter !== 'All' && effectiveStatus(i) !== filter) return false;
      if (q && !i.number.toLowerCase().includes(q) && !i.customerName.toLowerCase().includes(q))
        return false;
      // Drafts (no issue date) are never date-bounded out.
      if (i.issueDate && (fromMs != null || toMs != null)) {
        const ms = new Date(i.issueDate).getTime();
        if (fromMs != null && ms < fromMs) return false;
        if (toMs != null && ms > toMs) return false;
      }
      return true;
    });
  }, [invoices, filter, search, dateBounded, from, to]);

  const groups = useMemo(() => {
    const map = new Map<string, InvoiceSummary[]>();
    for (const i of filtered) {
      const arr = map.get(i.customerPartyId);
      if (arr) arr.push(i);
      else map.set(i.customerPartyId, [i]);
    }
    return [...map.values()];
  }, [filtered]);

  const changeStatus = (id: string, status: InvoiceStatusValue, msg: string) => {
    setStatus.mutate(
      { id, status },
      {
        onSuccess: () => showToast({ type: 'success', message: msg }),
        onError: () =>
          showToast({ type: 'error', message: 'Could not update the invoice — please try again.' }),
      },
    );
  };

  // Cancelling is asked for in a dialog rather than a confirm: it needs a
  // reason, and an exported invoice needs an acknowledgement too.
  const cancelInvoice = useCancelInvoice(orgId || null);

  // "Exported" is recorded by hand until a real accounting export exists — the
  // cancel and credit-note rules turn on it.
  const setExport = useSetInvoiceExport(orgId || null);
  const [exporting, setExporting] = useState<InvoiceSummary | null>(null);
  const [exportReference, setExportReference] = useState('');
  const markExported = (inv: InvoiceSummary, exported: boolean, reference?: string) =>
    setExport.mutate(
      { id: inv.id, exported, reference: reference?.trim() || null },
      {
        onSuccess: () => {
          setExporting(null);
          setExportReference('');
          showToast({
            type: 'success',
            message: exported ? `${inv.number} marked as exported.` : `${inv.number} no longer marked as exported.`,
          });
        },
        onError: (err) =>
          showToast({ type: 'error', message: err.message || 'Could not update the export mark.' }),
      },
    );
  const [cancelling, setCancelling] = useState<InvoiceSummary | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [accountingAcknowledged, setAccountingAcknowledged] = useState(false);

  const closeCancel = () => {
    setCancelling(null);
    setCancelReason('');
    setAccountingAcknowledged(false);
  };

  const confirmCancel = () => {
    if (!cancelling || cancelReason.trim() === '') return;
    const number = cancelling.number;
    cancelInvoice.mutate(
      {
        id: cancelling.id,
        reason: cancelReason.trim(),
        accountingCancellationAcknowledged: accountingAcknowledged,
      },
      {
        onSuccess: () => {
          closeCancel();
          showToast({
            type: 'success',
            message: `${number} cancelled — its charges are back in Create.`,
          });
        },
        // The server's message is the useful one here: paid, already cancelled,
        // or exported without the acknowledgement each need different wording.
        onError: (err) =>
          showToast({
            type: 'error',
            message: err instanceof Error ? err.message : `Could not cancel ${number}.`,
          }),
      },
    );
  };

  const saveBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // The PDF that was stored when the invoice was generated — the issued
  // document itself, not a fresh render of it.
  //
  // Why that matters rather than being a nicety: a re-render follows the
  // organisation's layout and letterhead as they are *now*, and it loses the
  // per-line order context (date, route, order ref) that only the generating
  // screen has, because the invoice line does not store it. A re-render of an
  // old invoice is therefore a different document from the one the customer
  // received.
  //
  // Rendering stays as the fallback for invoices raised before the PDF was
  // stored, and for the rare case where the upload failed at generation — the
  // client reports that as `null`, distinct from an error.
  //
  // Shared by Download and Open so the two can never disagree about which
  // document they are handing over. Errors are reported here; a null return
  // means the caller has nothing to show and has already been told why.
  const resolveInvoicePdf = async (
    id: string,
  ): Promise<{ blob: Blob; recreated: boolean } | null> => {
    try {
      const stored = await tmsApi.getInvoicePdfContent(id);
      // Re-typed: the browser decides between rendering and downloading on the
      // MIME type, and a blob typed anything else opens as a file save even in
      // a tab meant to display it.
      if (stored) {
        return { blob: new Blob([stored], { type: 'application/pdf' }), recreated: false };
      }
    } catch {
      showToast({ type: 'error', message: 'Could not fetch the invoice PDF.' });
      return null;
    }

    try {
      const inv = await tmsApi.getInvoice(id);
      // The customer's layout preference, so a recreated document matches the
      // design the issued one used. Best-effort: if the party cannot be read we
      // still produce the invoice on the organisation default rather than
      // failing outright.
      const customer = await directoryApi.getParty(inv.customerPartyId).catch(() => null);
      const template = docSource.templateFor(inv.docType, customer);
      if (!template) {
        showToast({ type: 'error', message: 'No document layout found for this invoice.' });
        return null;
      }
      const blob = await pdf(
        <DocumentPdf template={template} data={docSource.buildData(inv)} />,
      ).toBlob();
      // Say so rather than passing a re-creation off as the issued document.
      showToast({
        type: 'info',
        message: `No stored PDF for ${inv.number} — recreated it from the current layout.`,
      });
      return { blob, recreated: true };
    } catch {
      showToast({ type: 'error', message: 'Could not generate the PDF.' });
      return null;
    }
  };

  const downloadPdf = async (id: string, number: string) => {
    const resolved = await resolveInvoicePdf(id);
    if (resolved) saveBlob(resolved.blob, `${number}.pdf`);
  };

  // ── Email (Send / Resend) ───────────────────────────────────────────────
  // Asked in a confirm because an email can't be unsent, and so the operator
  // sees exactly who it will reach before it does.
  const sendEmail = useSendInvoiceEmail(orgId || null);
  // Which mailbox it will go from, said BEFORE the send — the same choice
  // Directory makes when it sends.
  const mailboxes = useOrganisationEmailConnections(orgId || null);
  const invoiceSender = mailboxes.data
    ? { known: true as const, connection: resolveEmailSender(mailboxes.data.connections, 'Invoices') }
    : { known: false as const };
  const [sending, setSending] = useState<InvoiceSummary | null>(null);
  const [sendBusy, setSendBusy] = useState(false);

  const confirmSend = async () => {
    if (!sending) return;
    const inv = sending;
    setSendBusy(true);
    try {
      // The server attaches the STORED PDF, which is the issued document (see
      // resolveInvoicePdf) — so it is never re-rendered over. Only an invoice
      // with nothing stored gets a recreated copy, uploaded so there is
      // something to attach; resolveInvoicePdf says when that happens.
      if (!inv.pdfGeneratedUtc) {
        const resolved = await resolveInvoicePdf(inv.id);
        if (!resolved) return; // already reported
        if (resolved.recreated) await tmsApi.uploadInvoicePdf(inv.id, resolved.blob);
      }
      const sent = await sendEmail.mutateAsync({ id: inv.id });
      // A failure may have flipped the mailbox to "needs reconnecting".
      void mailboxes.refetch();
      setSending(null);
      showToast({
        type: 'success',
        message: `${inv.number} emailed to ${sent.sentTo ?? 'the billing contacts'}.`,
      });
    } catch (err) {
      // The server's wording names the real problem — no billing contact, no
      // permission on the customer, the provider refusing — so it's shown as is.
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : `Could not send ${inv.number}.`,
      });
    } finally {
      setSendBusy(false);
    }
  };

  /**
   * Show the invoice in its own tab.
   *
   * <p>
   * The tab is opened FIRST, synchronously, while the click is still what the
   * browser is acting on — fetching the document first and opening afterwards
   * reads as an unprompted popup and gets blocked. It parks on `about:blank`
   * for the round-trip and is sent to the document once there is one, or closed
   * again if there turns out not to be.
   * </p>
   */
  const openPdf = async (id: string, number: string) => {
    const tab = window.open('', '_blank');
    if (!tab) {
      showToast({
        type: 'error',
        message: `Your browser blocked the new tab for ${number}. Allow pop-ups for this site, or use the download button.`,
      });
      return;
    }
    tab.document.write(
      `<title>${number}</title><body style="margin:0;font:14px system-ui;color:#64748b;padding:16px">Opening ${number}…</body>`,
    );

    const resolved = await resolveInvoicePdf(id);
    if (!resolved) {
      tab.close();
      return;
    }

    const url = URL.createObjectURL(resolved.blob);
    tab.location.href = url;
    // Revoked late: the tab needs the URL to still resolve when it loads, and
    // revoking on the next tick leaves it with a blank viewer.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const busy = invoicesQuery.isPending;
  const loadError = invoicesQuery.error;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden pb-4">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Invoice #, customer…"
              className="w-56 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
          <ToolbarDivider />
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
                  filter === f
                    ? 'border-slate-700 bg-slate-700 text-white'
                    : 'border-slate-300 bg-white text-slate-500 hover:border-slate-400',
                )}
              >
                {f}
              </button>
            ))}
          </div>

          {dateBounded && (
            <>
              <ToolbarDivider />
              <div className="flex items-center gap-1 text-[11px] text-slate-500">
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  aria-label="From date"
                  className="rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
                <span>–</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  aria-label="To date"
                  className="rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
                {(from || to) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFrom('');
                      setTo('');
                    }}
                    className="ml-0.5 rounded px-1 text-slate-400 hover:text-slate-700"
                    title="Clear dates"
                  >
                    ✕
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <GroupToggle grouped={grouped} onChange={setGrouped} flatLabel="All invoices" />
          <ToolbarDivider />
          <ExportMenu rows={filtered} />
          <AssistantButton onClick={onOpenAssistant} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead>
            <tr
              className={cn(
                'sticky top-0 z-10 text-[11px] uppercase tracking-wide',
                grouped
                  ? 'border-b border-slate-300 bg-slate-50 text-slate-400'
                  : 'bg-slate-700 text-white',
              )}
            >
              <Th>Invoice</Th>
              {/* Grouped, the customer is the band above each block; flat,
                  every row has to say who it is for. */}
              {!grouped && <Th>Customer</Th>}
              <Th>Orders</Th>
              <Th className="text-right">Subtotal</Th>
              <Th className="text-right">VAT</Th>
              <Th className="text-right">Total</Th>
              <Th>Status</Th>
              <Th>Date</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {!busy &&
              !loadError &&
              (grouped
                ? groups.map((g) => (
                    <CustomerGroup
                      key={g[0]!.customerPartyId}
                      rows={g}
                      expandedId={expandedId}
                      creditingId={creditingId}
                      editingId={editingId}
                      onToggle={toggleDetails}
                      onCredit={startCredit}
                      onEdit={startEdit}
                      onExport={setExporting}
                      onUnexport={(inv: InvoiceSummary) => markExported(inv, false)}
                      onCloseCredit={() => setCreditingId(null)}
                      onCloseEdit={() => setEditingId(null)}
                      organisationId={orgId || null}
                      billing={billingOf(g[0]!.customerPartyId)}
                      onChange={changeStatus}
                      onDownload={downloadPdf}
                      onOpen={openPdf}
                      onSend={setSending}
                      onCancel={setCancelling}
                    />
                  ))
                : filtered.map((i) => (
                    <InvoiceRow
                      key={i.id}
                      i={i}
                      showCustomer
                      expanded={expandedId === i.id}
                      crediting={creditingId === i.id}
                      editing={editingId === i.id}
                      onToggle={toggleDetails}
                      onCredit={startCredit}
                      onEdit={startEdit}
                      onExport={setExporting}
                      onUnexport={(inv: InvoiceSummary) => markExported(inv, false)}
                      onCloseCredit={() => setCreditingId(null)}
                      onCloseEdit={() => setEditingId(null)}
                      organisationId={orgId || null}
                      colSpan={9}
                      billing={billingOf(i.customerPartyId)}
                      onChange={changeStatus}
                      onDownload={downloadPdf}
                      onOpen={openPdf}
                      onSend={setSending}
                      onCancel={setCancelling}
                    />
                  )))}
            {busy && (
              <tr>
                <td
                  colSpan={grouped ? 8 : 9}
                  className="px-3 py-10 text-center text-xs text-slate-400"
                >
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
                  Loading invoices…
                </td>
              </tr>
            )}
            {!busy && loadError && (
              <tr>
                <td
                  colSpan={grouped ? 8 : 9}
                  className="px-3 py-10 text-center text-xs text-red-600"
                >
                  Couldn’t load invoices — {loadError.message}
                </td>
              </tr>
            )}
            {!busy && !loadError && filtered.length === 0 && (
              <tr>
                <td
                  colSpan={grouped ? 8 : 9}
                  className="px-3 py-10 text-center text-xs italic text-slate-400"
                >
                  No invoices match this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {exporting && (
        <ExportDialog
          invoice={exporting}
          reference={exportReference}
          onReferenceChange={setExportReference}
          busy={setExport.isPending}
          onConfirm={() => markExported(exporting, true, exportReference)}
          onClose={() => {
            setExporting(null);
            setExportReference('');
          }}
        />
      )}

      {cancelling && (
        <CancelInvoiceDialog
          invoice={cancelling}
          reason={cancelReason}
          onReasonChange={setCancelReason}
          acknowledged={accountingAcknowledged}
          onAcknowledgedChange={setAccountingAcknowledged}
          busy={cancelInvoice.isPending}
          onConfirm={confirmCancel}
          onClose={closeCancel}
        />
      )}

      <ConfirmDialog
        open={sending !== null}
        title={sending ? `Email ${sending.number}?` : ''}
        message={
          sending && (
            <SendConfirmMessage
              invoice={sending}
              billing={billingOf(sending.customerPartyId)}
              sender={invoiceSender}
            />
          )
        }
        confirmLabel="Send email"
        busy={sendBusy}
        onConfirm={() => void confirmSend()}
        onCancel={() => {
          if (!sendBusy) setSending(null);
        }}
      />
    </div>
  );
}

/** What the send confirmation says: who it reaches, and anything unusual about it. */
function SendConfirmMessage({
  invoice,
  billing,
  sender,
}: {
  invoice: InvoiceSummary;
  billing: BillingRecipients;
  /** The organisation mailbox it goes from; `connection: null` = My Stratum's sender. */
  sender: { known: true; connection: EmailConnection | null } | { known: false };
}) {
  const again = invoice.status === 'Sent' || invoice.status === 'Paid';
  return (
    <div className="space-y-2 text-sm text-slate-600">
      <SendFromLine sender={sender} />
      {billing.state === 'ok' ? (
        <>
          <p>The invoice PDF will be emailed to {invoice.customerName}’s billing contacts:</p>
          <ul className="list-disc space-y-0.5 pl-5 text-slate-800">
            {billing.emails.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </>
      ) : (
        // Unknown (still loading, or the lookup failed): the server looks the
        // contacts up itself and refuses if there are none, so this can go
        // ahead without the list rather than blocking on it.
        <p>The invoice PDF will be emailed to {invoice.customerName}’s billing contacts.</p>
      )}
      {again && <p>It has been sent before — this sends it again.</p>}
      {!invoice.pdfGeneratedUtc && (
        <p className="text-amber-700">
          No PDF was stored for this invoice, so a copy will be recreated from the current layout
          and attached.
        </p>
      )}
    </div>
  );
}

/**
 * "From: …" in the send confirmation. A mailbox that needs reconnecting is
 * said plainly: Directory will refuse the send rather than use another address.
 */
function SendFromLine({
  sender,
}: {
  sender: { known: true; connection: EmailConnection | null } | { known: false };
}) {
  if (!sender.known) return null;
  const c = sender.connection;
  if (!c) {
    return (
      <p>
        <span className="font-medium text-slate-800">From:</span> My Stratum’s sending address,
        under your company name.
      </p>
    );
  }
  const from = c.sendFromName ? `${c.sendFromName} <${c.sendFromAddress}>` : c.sendFromAddress;
  if (c.status === 'NeedsReconnect') {
    return (
      <p className="rounded-md bg-red-50 px-3 py-2 text-red-800 ring-1 ring-red-200">
        <span className="font-medium">From: {from}</span> — this mailbox needs reconnecting, so the
        email won’t be sent. An owner or admin can reconnect it in Settings → Organisation → Email
        sending.
      </p>
    );
  }
  return (
    <p>
      <span className="font-medium text-slate-800">From:</span> {from}
      <span className="text-slate-500">
        {' '}
        ({c.provider === 'Microsoft' ? 'Microsoft 365' : 'Google'})
      </span>
    </p>
  );
}

/** Shown wherever a customer has no billing contact to email invoices to. */
function NoBillingContactChip() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-800"
      title="This customer has no billing contact with an email address, so its invoices can't be emailed. Add one in Contacts."
    >
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      No billing contact
    </span>
  );
}

/**
 * The cancel dialog.
 *
 * <p>
 * Not a plain confirm, because voiding an invoice needs two things a yes/no
 * cannot carry: a REASON, which is kept on the invoice and in the audit trail —
 * a voided legal document with no stated reason is worse than no record at all
 * — and, when the invoice has already been exported, an acknowledgement that it
 * has been cancelled in the accounting system too. Nothing here can reach that
 * system, so the honest thing is to make somebody say they have done it, and to
 * store that statement next to the cancellation it belongs to.
 * </p>
 */
function CancelInvoiceDialog({
  invoice,
  reason,
  onReasonChange,
  acknowledged,
  onAcknowledgedChange,
  busy,
  onConfirm,
  onClose,
}: {
  invoice: InvoiceSummary;
  reason: string;
  onReasonChange: (v: string) => void;
  acknowledged: boolean;
  onAcknowledgedChange: (v: boolean) => void;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const exported = invoice.exportStatus === 'Exported';
  const canConfirm = reason.trim() !== '' && (!exported || acknowledged) && !busy;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Cancel ${invoice.number}`}
        className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
      >
        <div className="mb-2 flex items-center gap-2">
          <Ban className="h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-800">Cancel {invoice.number}?</h2>
        </div>

        <p className="mb-3 text-xs text-slate-600">
          {invoice.number} is a legal document. It keeps its number and stays in the list marked
          cancelled — the number is never reused, because a gap in the run is what an auditor asks
          about. The charges it billed go back to Create so the work can be invoiced again.
        </p>

        <label
          className="mb-1 block text-[11px] font-medium text-slate-600"
          htmlFor="cancel-reason"
        >
          Why is it being cancelled?
        </label>
        <textarea
          id="cancel-reason"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          rows={2}
          placeholder="e.g. billed the wrong customer"
          className="mb-3 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-700 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
        />

        {exported && (
          <label className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => onAcknowledgedChange(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
            />
            <span>
              This invoice has been exported to the accounting system. I confirm it has also been
              cancelled there — cancelling it here does not reach it.
            </span>
          </label>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-40"
          >
            {busy ? 'Cancelling…' : 'Cancel invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Record that the invoice is in the accounting system, with its reference
 * there if the operator has it. A dialog rather than a one-click action: the
 * mark changes what cancel and credit allow, so it is worth a deliberate step.
 */
function ExportDialog({
  invoice,
  reference,
  onReferenceChange,
  busy,
  onConfirm,
  onClose,
}: {
  invoice: InvoiceSummary;
  reference: string;
  onReferenceChange: (v: string) => void;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Mark ${invoice.number} as exported`}
        className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
      >
        <div className="mb-2 flex items-center gap-2">
          <FileUp className="h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-800">Mark {invoice.number} as exported?</h2>
        </div>
        <p className="mb-3 text-xs text-slate-600">
          Records that this {invoice.docType === 'Credit' ? 'credit note' : 'invoice'} is in your accounting
          system. Once exported, cancelling it asks you to confirm it was cancelled there too, and an unpaid
          invoice can be corrected with a credit note.
        </p>
        <label className="mb-1 block text-[11px] font-medium text-slate-600" htmlFor="export-reference">
          Reference in the accounting system (optional)
        </label>
        <input
          id="export-reference"
          value={reference}
          maxLength={128}
          onChange={(e) => onReferenceChange(e.target.value)}
          placeholder="e.g. the Xero / Sage invoice number"
          className="mb-3 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-700 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Mark as exported'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Purple "Export to" button — CSV downloads the filtered rows; Excel/PDF later. */
function ExportMenu({ rows }: { rows: InvoiceSummary[] }) {
  const [open, setOpen] = useState(false);
  const exportCsv = () => {
    const header = [
      'Invoice',
      'Type',
      'Customer',
      'Orders',
      'Subtotal',
      'VAT',
      'Total',
      'Status',
      'Issue date',
      'Due date',
    ];
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = rows.map((i) =>
      [
        i.number,
        i.docType,
        i.customerName,
        i.orders,
        i.subtotal.toFixed(2),
        i.vatTotal.toFixed(2),
        i.total.toFixed(2),
        effectiveStatus(i),
        i.issueDate ?? '',
        i.dueDate ?? '',
      ]
        .map((c) => esc(String(c)))
        .join(','),
    );
    const csv = [header.map(esc).join(','), ...lines].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invoices.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-sm bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700"
      >
        <Download className="h-3.5 w-3.5" />
        Export to
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 z-20 mt-1 w-32 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                exportCsv();
              }}
              className="block w-full px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
            >
              CSV
            </button>
            {(['Excel', 'PDF'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => {
                  setOpen(false);
                  showToast({ type: 'info', message: `${f} export — coming soon.` });
                }}
                className="block w-full px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
              >
                {f}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CustomerGroup({
  rows,
  expandedId,
  creditingId,
  editingId,
  onToggle,
  onCredit,
  onEdit,
  onExport,
  onUnexport,
  onCloseCredit,
  onCloseEdit,
  organisationId,
  billing,
  onChange,
  onDownload,
  onOpen,
  onSend,
  onCancel,
}: {
  rows: InvoiceSummary[];
  expandedId: string | null;
  creditingId: string | null;
  editingId: string | null;
  onToggle: (id: string) => void;
  onCredit: (invoice: InvoiceSummary) => void;
  onEdit: (invoice: InvoiceSummary) => void;
  onExport: (invoice: InvoiceSummary) => void;
  onUnexport: (invoice: InvoiceSummary) => void;
  onCloseCredit: () => void;
  onCloseEdit: () => void;
  organisationId: string | null;
  billing: BillingRecipients;
  onChange: (id: string, status: InvoiceStatusValue, msg: string) => void;
  onDownload: (id: string, number: string) => void;
  onOpen: (id: string, number: string) => void;
  onSend: (invoice: InvoiceSummary) => void;
  onCancel: (invoice: InvoiceSummary) => void;
}) {
  const c = rows[0]!;
  // Credit notes count against the customer's total — they reduce what is owed.
  const totalAll = rows
    .filter((i) => i.status !== 'Cancelled')
    .reduce((sum, i) => sum + (i.docType === 'Credit' ? -i.total : i.total), 0);
  // Grouped, the warning lives once on the customer band rather than on every
  // row beneath it — unless every invoice there is cancelled, when it's moot.
  const warn = billing.state === 'none' && rows.some((i) => i.status !== 'Cancelled');
  return (
    <>
      <tr>
        <td colSpan={8} className="p-0">
          <CustomerGroupHeader
            name={c.customerName}
            code={c.customerCode ?? undefined}
            meta={`${rows.length} invoice${rows.length === 1 ? '' : 's'}`}
            action={warn ? <NoBillingContactChip /> : undefined}
          />
        </td>
      </tr>
      {rows.map((i) => (
        <InvoiceRow
          key={i.id}
          i={i}
          expanded={expandedId === i.id}
          crediting={creditingId === i.id}
          editing={editingId === i.id}
          onToggle={onToggle}
          onCredit={onCredit}
          onEdit={onEdit}
          onExport={onExport}
          onUnexport={onUnexport}
          onCloseCredit={onCloseCredit}
          onCloseEdit={onCloseEdit}
          organisationId={organisationId}
          colSpan={8}
          billing={billing}
          onChange={onChange}
          onDownload={onDownload}
          onOpen={onOpen}
          onSend={onSend}
          onCancel={onCancel}
        />
      ))}
      <tr className="border-b border-slate-300 bg-slate-50/60 text-[11px] font-semibold text-slate-600">
        <td className="px-3 py-1.5 italic">{rows.length} invoices · subtotal</td>
        <td colSpan={3} />
        <td className="px-3 py-1.5 text-right tabular-nums">
          {formatMoney(totalAll, c.currencyIsoCode)}
        </td>
        <td colSpan={3} />
      </tr>
    </>
  );
}

function InvoiceRow({
  i,
  showCustomer = false,
  expanded,
  crediting,
  editing,
  onToggle,
  onCredit,
  onEdit,
  onExport,
  onUnexport,
  onCloseCredit,
  onCloseEdit,
  organisationId,
  colSpan,
  billing,
  onChange,
  onDownload,
  onOpen,
  onSend,
  onCancel,
}: {
  i: InvoiceSummary;
  /** Flat view only — grouped rows sit under a customer band already. */
  showCustomer?: boolean;
  /** Details open under this row. */
  expanded: boolean;
  /** The credit note builder is open under this row. */
  crediting: boolean;
  /** The draft editor is open under this row. */
  editing: boolean;
  onToggle: (id: string) => void;
  onCredit: (invoice: InvoiceSummary) => void;
  onEdit: (invoice: InvoiceSummary) => void;
  onExport: (invoice: InvoiceSummary) => void;
  onUnexport: (invoice: InvoiceSummary) => void;
  onCloseCredit: () => void;
  onCloseEdit: () => void;
  organisationId: string | null;
  /** Columns the details row spans — the table is one wider when flat. */
  colSpan: number;
  billing: BillingRecipients;
  onChange: (id: string, status: InvoiceStatusValue, msg: string) => void;
  onDownload: (id: string, number: string) => void;
  onOpen: (id: string, number: string) => void;
  onSend: (invoice: InvoiceSummary) => void;
  onCancel: (invoice: InvoiceSummary) => void;
}) {
  const status = effectiveStatus(i);
  const sign = i.docType === 'Credit' ? -1 : 1;
  return (
    <Fragment>
    <tr
      className={cn(
        'border-b border-slate-300 last:border-0 hover:bg-slate-50',
        expanded && 'bg-sky-50/60',
      )}
      onDoubleClick={() => onToggle(i.id)}
    >
      <td className="whitespace-nowrap px-3 py-1.5">
        <button
          type="button"
          onClick={() => onToggle(i.id)}
          aria-expanded={expanded}
          aria-label={expanded ? `Hide details of ${i.number}` : `Show details of ${i.number}`}
          title={expanded ? 'Hide details' : 'Show the lines, VAT and changes'}
          className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-200 hover:text-slate-700"
        >
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-90')} />
        </button>
        <button
          type="button"
          onClick={() => void onOpen(i.id, i.number)}
          title={
            i.pdfGeneratedUtc
              ? `Open ${i.number} in a new tab`
              : `No PDF stored for ${i.number} — opens a copy recreated from the current layout`
          }
          className="font-medium text-sky-700 hover:underline"
        >
          {i.number}
        </button>
        <div className="mt-0.5">
          {i.docType === 'Credit' ? (
            <>
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-800">
                Credit
              </span>
              {i.creditNoteOfNumber && (
                <span className="ml-1 text-[10px] text-slate-400">for {i.creditNoteOfNumber}</span>
              )}
            </>
          ) : (
            <span className="text-[10px] text-slate-400">{i.docType}</span>
          )}
        </div>
      </td>
      {showCustomer && (
        <td className="max-w-[14rem] px-3 py-1.5">
          <div className="truncate font-medium text-slate-700" title={i.customerName}>
            {i.customerName}
          </div>
          {i.customerCode && <div className="text-[10px] text-slate-400">{i.customerCode}</div>}
          {billing.state === 'none' && status !== 'Cancelled' && (
            <div className="mt-0.5">
              <NoBillingContactChip />
            </div>
          )}
        </td>
      )}
      <td className="max-w-[12rem] truncate px-3 py-1.5 text-slate-500" title={i.orders}>
        {i.orders}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-600">
        {formatMoney(sign * i.subtotal, i.currencyIsoCode)}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-600">
        {formatMoney(sign * i.vatTotal, i.currencyIsoCode)}
      </td>
      <td
        className={cn(
          'whitespace-nowrap px-3 py-1.5 text-right font-semibold tabular-nums',
          sign < 0 ? 'text-rose-700' : 'text-slate-800',
        )}
      >
        {formatMoney(sign * i.total, i.currencyIsoCode)}
      </td>
      <td className="px-3 py-1.5">
        <StatusBadge status={status} />
        {status === 'Overdue' && i.overdueDays != null && (
          <span className="ml-1 text-[10px] text-red-500">{i.overdueDays}d</span>
        )}
        {i.exportStatus === 'Exported' && (
          <span
            className="ml-1 rounded bg-sky-50 px-1.5 py-0.5 text-[9px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200"
            title="In the accounting system"
          >
            Exported
          </span>
        )}
        {(i.creditedTotal ?? 0) > 0 && (
          <span
            className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-[9px] font-medium text-rose-700 ring-1 ring-inset ring-rose-200"
            title={`Credit notes: ${i.creditNoteNumbers ?? ''}`}
          >
            {(i.creditedTotal ?? 0) >= i.total ? 'Credited' : 'Part-credited'}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{formatDate(i.issueDate)}</td>
      <td className="px-3 py-1.5">
        <div className="flex items-center justify-end">
          <RowActionsMenu
            label={i.number}
            actions={rowActions({
              i,
              status,
              billing,
              onChange,
              onDownload,
              onOpen,
              onSend,
              onCancel,
              onCredit,
              onEdit,
              onExport,
              onUnexport,
            })}
          />
        </div>
      </td>
    </tr>
    {crediting && (
      <tr>
        <td colSpan={colSpan} className="p-0">
          <CreditNoteBuilder
            invoiceId={i.id}
            organisationId={organisationId}
            onCancel={onCloseCredit}
            onDone={onCloseCredit}
          />
        </td>
      </tr>
    )}
    {editing && (
      <tr>
        <td colSpan={colSpan} className="p-0">
          <DraftInvoiceEditor
            invoiceId={i.id}
            organisationId={organisationId}
            onCancel={onCloseEdit}
            onDone={onCloseEdit}
          />
        </td>
      </tr>
    )}
    {expanded && !crediting && !editing && (
      <tr>
        <td colSpan={colSpan} className="p-0">
          <InvoiceDetails invoiceId={i.id} organisationId={organisationId} />
        </td>
      </tr>
    )}
    </Fragment>
  );
}

/**
 * What this invoice can have done to it, as DATA for the "⋮" menu.
 *
 * <p>
 * Every action is listed in every status, with `disabledReason` carrying why it
 * cannot be taken right now rather than the item vanishing: an operator asking
 * "why can't I cancel this?" is better served by a greyed row that says the
 * invoice is paid than by no row at all. It also means the menu reads the same
 * shape on every line, which is what makes it learnable.
 * </p>
 */
function rowActions({
  i,
  status,
  billing,
  onChange,
  onDownload,
  onOpen,
  onSend,
  onCancel,
  onCredit,
  onEdit,
  onExport,
  onUnexport,
}: {
  i: InvoiceSummary;
  status: InvoiceStatus;
  billing: BillingRecipients;
  onChange: (id: string, status: InvoiceStatusValue, msg: string) => void;
  onDownload: (id: string, number: string) => void;
  onOpen: (id: string, number: string) => void;
  onSend: (invoice: InvoiceSummary) => void;
  onCancel: (invoice: InvoiceSummary) => void;
  onCredit: (invoice: InvoiceSummary) => void;
  onEdit: (invoice: InvoiceSummary) => void;
  onExport: (invoice: InvoiceSummary) => void;
  onUnexport: (invoice: InvoiceSummary) => void;
}): RowAction[] {
  const cancelled = status === 'Cancelled';
  const unsendable = cancelled ? 'This invoice is cancelled.' : undefined;

  /** Why a status move is unavailable, or undefined when it is. */
  const only = (when: boolean, reason: string) => unsendable ?? (when ? undefined : reason);

  // Emailing needs someone to email. Only a KNOWN absence disables it — while
  // the contacts load, or if they couldn't be read, the server decides.
  const noBillingContact =
    billing.state === 'none'
      ? `${i.customerName} has no billing contact — add one in Contacts to email this invoice.`
      : undefined;
  /** Status reason first (it's the more basic problem), then the recipient one. */
  const emailable = (when: boolean, reason: string) => only(when, reason) ?? noBillingContact;

  return [
    {
      id: 'open',
      label: 'Open in a new tab',
      icon: <ExternalLink className="h-3.5 w-3.5 text-slate-500" />,
      onSelect: () => void onOpen(i.id, i.number),
    },
    {
      id: 'download',
      label: i.pdfGeneratedUtc ? 'Download PDF' : 'Download PDF (recreated)',
      icon: <FileDown className="h-3.5 w-3.5 text-slate-500" />,
      onSelect: () => void onDownload(i.id, i.number),
    },
    {
      id: 'issue',
      label: 'Issue',
      icon: <FileCheck2 className="h-3.5 w-3.5 text-emerald-600" />,
      onSelect: () => onChange(i.id, 'Issued', `${i.number} issued.`),
      disabledReason: only(status === 'Draft', 'Already issued.'),
    },
    {
      id: 'send',
      label: 'Send by email',
      icon: <Send className="h-3.5 w-3.5 text-slate-500" />,
      onSelect: () => onSend(i),
      disabledReason: emailable(status === 'Issued', 'Only an issued invoice can be sent.'),
    },
    {
      // For an invoice that reached the customer some other way — posted, or
      // emailed by hand. Kept alongside Send so a customer with no billing
      // contact can still have their invoices recorded as delivered.
      id: 'mark-sent',
      label: 'Mark as sent (no email)',
      icon: <MailCheck className="h-3.5 w-3.5 text-slate-500" />,
      onSelect: () => onChange(i.id, 'Sent', `${i.number} marked as sent.`),
      disabledReason: only(status === 'Issued', 'Only an issued invoice can be marked sent.'),
    },
    {
      id: 'resend',
      label: 'Resend by email',
      icon: <RefreshCw className="h-3.5 w-3.5 text-slate-500" />,
      onSelect: () => onSend(i),
      // Paid included: a customer asking for another copy is the usual reason.
      disabledReason: emailable(
        status === 'Sent' || status === 'Overdue' || status === 'Paid',
        'Nothing has been sent yet.',
      ),
    },
    {
      id: 'chase',
      label: 'Chase',
      icon: <Bell className="h-3.5 w-3.5 text-amber-600" />,
      onSelect: () =>
        showToast({
          type: 'info',
          message: `Chasing ${i.number} isn’t available yet — use Resend to email it again.`,
        }),
      disabledReason: only(status === 'Overdue', 'Not overdue.'),
    },
    {
      id: 'paid',
      label: 'Mark paid',
      icon: <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />,
      onSelect: () => onChange(i.id, 'Paid', `${i.number} marked paid.`),
      disabledReason: only(
        status === 'Issued' || status === 'Sent' || status === 'Overdue',
        status === 'Paid' ? 'Already paid.' : 'Issue the invoice first.',
      ),
    },
    // ── The way back from each step ──────────────────────────────────────
    {
      id: 'unsend',
      label: 'Not sent after all',
      icon: <Undo2 className="h-3.5 w-3.5 text-slate-500" />,
      onSelect: () => onChange(i.id, 'Issued', `${i.number} moved back to issued.`),
      disabledReason: only(status === 'Sent' || status === 'Overdue', 'It has not been sent.'),
    },
    {
      id: 'unpay',
      label: 'Not paid after all',
      icon: <Undo2 className="h-3.5 w-3.5 text-slate-500" />,
      onSelect: () => onChange(i.id, 'Sent', `${i.number} moved back to sent.`),
      disabledReason: only(status === 'Paid', 'It is not marked paid.'),
    },
    i.exportStatus === 'Exported'
      ? {
          id: 'unexport',
          label: 'Not exported after all',
          icon: <Undo2 className="h-3.5 w-3.5 text-slate-500" />,
          onSelect: () => onUnexport(i),
        }
      : {
          id: 'export',
          label: 'Mark as exported',
          icon: <FileUp className="h-3.5 w-3.5 text-sky-600" />,
          onSelect: () => onExport(i),
          disabledReason:
            i.docType === 'Proforma'
              ? 'A proforma is not a tax document and is not exported.'
              : status === 'Draft'
                ? 'Issue it first.'
                : undefined,
        },
    {
      id: 'edit',
      label: i.docType === 'Proforma' ? 'Edit proforma' : 'Edit draft',
      icon: <FilePenLine className="h-3.5 w-3.5 text-violet-600" />,
      onSelect: () => onEdit(i),
      // Issued is a legal document — cancel it or credit it instead.
      disabledReason:
        i.docType === 'Credit'
          ? 'A credit note cannot be edited.'
          : status !== 'Draft'
            ? 'Only a draft can be edited — an issued invoice is cancelled or credited.'
            : undefined,
    },
    {
      id: 'credit',
      label: 'Raise credit note',
      icon: <ReceiptText className="h-3.5 w-3.5 text-rose-600" />,
      onSelect: () => onCredit(i),
      // Paid is credited; unpaid is cancelled — unless it is already in the
      // accounting system, where its VAT may be declared.
      disabledReason: creditReason(i, status),
    },
    {
      id: 'cancel',
      label: i.docType === 'Credit' ? 'Cancel credit note' : 'Cancel invoice',
      icon: <Ban className="h-3.5 w-3.5" />,
      onSelect: () => onCancel(i),
      destructive: true,
      // Paid is the line: money has moved, and only a credit note reverses it.
      disabledReason: cancelled
        ? 'Already cancelled.'
        : status === 'Paid'
          ? 'A paid invoice cannot be cancelled — raise a credit note instead.'
          : (i.creditedTotal ?? 0) > 0
            ? `This invoice has been credited (${i.creditNoteNumbers ?? 'credit notes'}) — cancel those first.`
            : undefined,
    },
  ];
}

/** Why this row cannot be credited right now, or undefined when it can. */
function creditReason(i: InvoiceSummary, status: InvoiceStatus): string | undefined {
  if (i.docType === 'Credit') return 'A credit note cannot itself be credited.';
  if (i.docType === 'Proforma') return 'A proforma bills nothing.';
  if (status === 'Cancelled') return 'This invoice is cancelled.';
  if (status === 'Draft') return 'A draft is not issued yet — cancel it instead.';
  if (status !== 'Paid' && i.exportStatus !== 'Exported')
    return 'Unpaid — cancel it instead. Credit notes are for paid invoices, or unpaid ones already exported.';
  if ((i.creditedTotal ?? 0) >= i.total) return 'Already credited in full.';
  return undefined;
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-1.5 text-left font-semibold', className)}>{children}</th>;
}
